import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert } from 'react-native';
import { Text, Card, Button, ActivityIndicator, DataTable, Searchbar, Menu } from 'react-native-paper';
import PeriodoChip from '../../components/PeriodoChip';
import { Ionicons } from '@expo/vector-icons';
import { formatCurrency } from '../../utils/formatters';
import api from '../../services/api';
import { exportService } from '../../services/exportService';

const PERIODOS = [
  { id: 'hoy', label: 'Hoy' },
  { id: 'esta_semana', label: 'Esta Semana' },
  { id: 'este_mes', label: 'Este Mes' },
  { id: 'ultimo_trimestre', label: 'Último Trimestre' },
];

const ESTADO_COLOR = {
  'Entregado': '#28a745', 'En Ruta': '#ffc107', 'Preparado': '#17a2b8', 'Pendiente': '#dc3545',
};

const ReporteConsolidadoEntregasScreen = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuVisible, setMenuVisible] = useState(false);
  const [periodoActivo, setPeriodoActivo] = useState('este_mes');
  const [page, setPage] = useState(0);
  const itemsPerPage = 25;

  useEffect(() => { cargarReporte(); }, []);
  useEffect(() => { filtrarDatos(); }, [searchQuery, data]);
  useEffect(() => { setPage(0); }, [filteredData.length, periodoActivo]);

  const cargarReporte = async (periodo = periodoActivo) => {
    try {
      setLoading(true);
      const response = await api.get(`/reportes/consolidado-entregas?periodo=${periodo}`);
      setData(response);
      setFilteredData(response.entregas || []);
    } catch (error) {
      console.error('Error:', error);
      Alert.alert('Error', 'No se pudo cargar el reporte');
    } finally { setLoading(false); }
  };

  const cambiarPeriodo = (p) => { setPeriodoActivo(p); cargarReporte(p); };

  const filtrarDatos = () => {
    if (!data?.entregas) { setFilteredData([]); return; }
    if (!searchQuery.trim()) { setFilteredData(data.entregas); return; }
    const q = searchQuery.toLowerCase();
    setFilteredData(data.entregas.filter(e =>
      (e.cliente_nombre || '').toLowerCase().includes(q) ||
      (e.vendedor_nombre || '').toLowerCase().includes(q) ||
      (e.repartidor_nombre || '').toLowerCase().includes(q)
    ));
  };

  const exportarDatos = async (formato) => {
    setMenuVisible(false);
    if (!filteredData.length) return Alert.alert('Error', 'No hay datos');
    const headers = ['Venta', 'Fecha', 'Cliente', 'Vendedor', 'Monto', 'Estado Entrega', 'Firma', 'Repartidor'];
    const exportData = filteredData.map(e => ({
      'Venta': e.venta_id, 'Fecha': e.fecha_venta, 'Cliente': e.cliente_nombre || '-',
      'Vendedor': e.vendedor_nombre || '-', 'Monto': parseFloat(e.monto_venta || 0).toFixed(2),
      'Estado Entrega': e.estado_entrega, 'Firma': e.tiene_firma ? 'Sí' : 'No',
      'Repartidor': e.repartidor_nombre || '-'
    }));
    await exportService.exportData(formato, exportData, 'consolidado_entregas', headers, 'Reporte Consolidado de Entregas');
  };

  const res = data?.resumen || {};

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={{ flex: 1 }}>
          <Text style={styles.title}>Consolidado de Entregas</Text>
          <Text style={styles.subtitle}>Vendido vs Entregado</Text>
        </View>
        <Menu visible={menuVisible} onDismiss={() => setMenuVisible(false)}
          anchor={<Button icon="download" mode="outlined" onPress={() => setMenuVisible(true)} disabled={loading}>Exportar</Button>}>
          <Menu.Item title="Excel" onPress={() => exportarDatos('excel')} />
          <Menu.Item title="PDF" onPress={() => exportarDatos('pdf')} />
          <Menu.Item title="CSV" onPress={() => exportarDatos('csv')} />
        </Menu>
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filtrosContainer} contentContainerStyle={styles.filtrosContent}>
        <View style={styles.filtros}>
          {PERIODOS.map(p => (
            <PeriodoChip
              key={p.id}
              active={periodoActivo === p.id}
              onPress={() => !loading && cambiarPeriodo(p.id)}
            >
              {p.label}
            </PeriodoChip>
          ))}
        </View>
      </ScrollView>

      {loading ? (
        <View style={styles.loader}>
          <ActivityIndicator size="large" color="#0066cc" />
          <Text style={{ marginTop: 10, color: '#666' }}>Generando reporte...</Text>
        </View>
      ) : (
      <ScrollView>
        {/* KPIs */}
        <Card style={styles.card}>
          <Card.Content>
            <Text style={styles.cardTitle}>Indicadores</Text>
            <View style={styles.kpiGrid}>
              <View style={[styles.kpiItem, { borderLeftColor: '#007bff' }]}>
                <Text style={[styles.kpiValue, { color: '#007bff' }]}>{res.total_vendido || 0}</Text>
                <Text style={styles.kpiLabel}>Vendidos</Text>
              </View>
              <View style={[styles.kpiItem, { borderLeftColor: '#28a745' }]}>
                <Text style={[styles.kpiValue, { color: '#28a745' }]}>{res.total_entregado || 0}</Text>
                <Text style={styles.kpiLabel}>Entregados</Text>
              </View>
              <View style={[styles.kpiItem, { borderLeftColor: '#ffc107' }]}>
                <Text style={[styles.kpiValue, { color: '#ffc107' }]}>{res.en_ruta || 0}</Text>
                <Text style={styles.kpiLabel}>En Ruta</Text>
              </View>
              <View style={[styles.kpiItem, { borderLeftColor: '#dc3545' }]}>
                <Text style={[styles.kpiValue, { color: '#dc3545' }]}>{res.pendientes || 0}</Text>
                <Text style={styles.kpiLabel}>Pendientes</Text>
              </View>
            </View>

            <View style={styles.barContainer}>
              <View style={styles.barBg}>
                <View style={[styles.barFill, { width: `${res.porcentaje_entrega || 0}%` }]} />
              </View>
              <Text style={styles.barText}>{res.porcentaje_entrega || 0}% entregado</Text>
            </View>

            <View style={styles.montosRow}>
              <View>
                <Text style={styles.montoLabel}>Monto Vendido</Text>
                <Text style={[styles.montoValue, { color: '#007bff' }]}>{formatCurrency(res.monto_vendido)}</Text>
              </View>
              <View>
                <Text style={styles.montoLabel}>Monto Entregado</Text>
                <Text style={[styles.montoValue, { color: '#28a745' }]}>{formatCurrency(res.monto_entregado)}</Text>
              </View>
              <View>
                <Text style={styles.montoLabel}>Con Firma</Text>
                <Text style={[styles.montoValue, { color: '#17a2b8' }]}>{res.con_firma || 0}</Text>
              </View>
            </View>
          </Card.Content>
        </Card>

        <Searchbar placeholder="Buscar..." onChangeText={setSearchQuery} value={searchQuery} style={styles.searchbar} />

        <ScrollView horizontal style={{ paddingHorizontal: 16 }}>
          <DataTable style={{ backgroundColor: '#fff', minWidth: 850 }}>
            <DataTable.Header>
              <DataTable.Title style={{ minWidth: 50 }}>#</DataTable.Title>
              <DataTable.Title style={{ minWidth: 80 }}>Fecha</DataTable.Title>
              <DataTable.Title style={{ minWidth: 140 }}>Cliente</DataTable.Title>
              <DataTable.Title style={{ minWidth: 110 }}>Vendedor</DataTable.Title>
              <DataTable.Title numeric style={{ minWidth: 90 }}>Monto</DataTable.Title>
              <DataTable.Title style={{ minWidth: 90 }}>Estado</DataTable.Title>
              <DataTable.Title style={{ minWidth: 60 }}>Firma</DataTable.Title>
              <DataTable.Title style={{ minWidth: 110 }}>Repartidor</DataTable.Title>
            </DataTable.Header>
            {filteredData.slice(page * itemsPerPage, (page + 1) * itemsPerPage).map((e, idx) => (
              <DataTable.Row key={page * itemsPerPage + idx}>
                <DataTable.Cell style={{ minWidth: 50 }}>{e.venta_id}</DataTable.Cell>
                <DataTable.Cell style={{ minWidth: 80 }}>{e.fecha_venta}</DataTable.Cell>
                <DataTable.Cell style={{ minWidth: 140 }}>{e.cliente_nombre || '-'}</DataTable.Cell>
                <DataTable.Cell style={{ minWidth: 110 }}>{e.vendedor_nombre || '-'}</DataTable.Cell>
                <DataTable.Cell numeric style={{ minWidth: 90 }}>{formatCurrency(e.monto_venta)}</DataTable.Cell>
                <DataTable.Cell style={{ minWidth: 90 }}>
                  <Text style={{ color: ESTADO_COLOR[e.estado_entrega] || '#666', fontWeight: '600', fontSize: 12 }}>
                    {e.estado_entrega}
                  </Text>
                </DataTable.Cell>
                <DataTable.Cell style={{ minWidth: 60 }}>
                  <Ionicons name={e.tiene_firma ? 'checkmark-circle' : 'close-circle'} size={18} color={e.tiene_firma ? '#28a745' : '#ccc'} />
                </DataTable.Cell>
                <DataTable.Cell style={{ minWidth: 110 }}>{e.repartidor_nombre || '-'}</DataTable.Cell>
              </DataTable.Row>
            ))}
            {filteredData.length > itemsPerPage && (
              <DataTable.Pagination
                page={page}
                numberOfPages={Math.ceil(filteredData.length / itemsPerPage)}
                onPageChange={setPage}
                label={`${page * itemsPerPage + 1}-${Math.min((page + 1) * itemsPerPage, filteredData.length)} de ${filteredData.length}`}
                showFastPaginationControls
                numberOfItemsPerPage={itemsPerPage}
              />
            )}
          </DataTable>
        </ScrollView>

        {filteredData.length === 0 && (
          <View style={styles.empty}><Ionicons name="car-outline" size={50} color="#ccc" /><Text style={{ color: '#999', marginTop: 10 }}>Sin entregas</Text></View>
        )}
        <View style={{ height: 40 }} />
      </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loader: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', padding: 16, backgroundColor: '#fff', elevation: 2 },
  title: { fontSize: 20, fontWeight: 'bold', color: '#333' },
  subtitle: { fontSize: 14, color: '#666', marginTop: 2 },
  filtrosContainer: { backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#eee', height: 56, flexGrow: 0, flexShrink: 0 },
  filtrosContent: { alignItems: 'center', paddingVertical: 10 },
  filtros: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16 },
  chip: { marginRight: 8, backgroundColor: '#f0f0f0' },
  chipActivo: { backgroundColor: '#4CAF50' },
  chipTexto: { color: '#333' },
  chipTextoActivo: { color: '#fff', fontWeight: '600' },
  card: { margin: 16, marginBottom: 8, elevation: 2 },
  cardTitle: { fontSize: 16, fontWeight: 'bold', marginBottom: 12 },
  kpiGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  kpiItem: { flex: 1, minWidth: '22%', backgroundColor: '#f9f9f9', padding: 10, borderRadius: 6, alignItems: 'center', borderLeftWidth: 3 },
  kpiValue: { fontSize: 20, fontWeight: 'bold' },
  kpiLabel: { fontSize: 10, color: '#888', marginTop: 2 },
  barContainer: { marginBottom: 12 },
  barBg: { height: 8, backgroundColor: '#e0e0e0', borderRadius: 4, overflow: 'hidden' },
  barFill: { height: '100%', backgroundColor: '#28a745', borderRadius: 4 },
  barText: { fontSize: 11, color: '#666', marginTop: 4, textAlign: 'center' },
  montosRow: { flexDirection: 'row', justifyContent: 'space-between' },
  montoLabel: { fontSize: 10, color: '#888' },
  montoValue: { fontSize: 14, fontWeight: '700' },
  searchbar: { margin: 16, marginBottom: 8 },
  empty: { alignItems: 'center', padding: 40 },
});

export default ReporteConsolidadoEntregasScreen;
